import { computed, type ResourceRef } from '@angular/core';
import { rxResource, toObservable } from '@angular/core/rxjs-interop';
import { filter, type Observable, takeUntil } from 'rxjs';

export type KeyedStreamOptions<T, K> = {
  /** The identity the value belongs to; `undefined` means there is nothing to hold. */
  readonly params: () => K | undefined;
  /** The value for a key as a live stream: the initial fetch and every later change. */
  readonly stream: (key: K) => Observable<T>;
};

/**
 * A resource whose value is a stream keyed on an identity.
 *
 * `rxResource` already re-subscribes when the key changes to another key, but
 * when the key goes away (`undefined`) it only drops the value and leaves the
 * previous stream subscribed. A live stream owns resources (a realtime
 * channel), so the stream is ended here the moment its key is gone, and the
 * subscription's teardown runs at once.
 */
export function keyedStreamResource<T, K>(
  options: KeyedStreamOptions<T, K>
): ResourceRef<T | undefined> {
  const gone$ = toObservable(computed(options.params)).pipe(filter((key) => key === undefined));
  return rxResource<T, K | undefined>({
    params: options.params,
    stream: ({ params: key }) => options.stream(key).pipe(takeUntil(gone$)),
  });
}
